import { MoreVertical, Plus } from 'lucide-react'
import { KanbanCard } from './KanbanCard'
import { Projeto } from '../tipos/projeto'

interface KanbanColumnProps {
  id: string
  title: string
  statusCodigo: number
  projetos: Projeto[]
  onCardClick: (projeto: Projeto) => void
  isOver?: boolean
  isReadOnly?: boolean
}

// Colunas com cor padronizada (cinza)
const COLUMN_COLORS = {
  bg: 'bg-gray-50',
  border: 'border-gray-200',
  text: 'text-gray-700',
}

export function KanbanColumn({ id: _id, title, statusCodigo: _statusCodigo, projetos, onCardClick, isOver = false, isReadOnly = false }: KanbanColumnProps) {
  const cardCount = projetos.length
  const isColumnOver = isOver && !isReadOnly

  return (
    <div
      className={`flex-shrink-0 ${COLUMN_COLORS.bg} rounded-lg flex flex-col transition-all duration-200 border ${COLUMN_COLORS.border} ${
        isColumnOver ? 'ring-2 ring-brand-500 ring-offset-1' : ''
      }`}
      style={{ 
        width: '288px', // w-72 = 288px (18rem)
        minWidth: '288px',
        height: 'calc(100vh - 240px)',
        maxHeight: 'calc(100vh - 240px)',
        // Garantir que o flex funcione corretamente
        minHeight: 0
      }}
    >
      {/* Cabeçalho da Coluna estilo Trello */}
      <div className={`flex-shrink-0 px-3 py-2.5 bg-white/60 rounded-t-lg border-b ${COLUMN_COLORS.border}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h3 className={`text-sm font-semibold ${COLUMN_COLORS.text} truncate`}>{title}</h3>
            <span className={`flex-shrink-0 text-xs ${COLUMN_COLORS.text} bg-white/80 px-1.5 py-0.5 rounded-full font-medium`}>
              {cardCount}
            </span>
          </div>
          {!isReadOnly && (
            <div className="flex items-center gap-1">
              <button
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                title="Adicionar card"
                onClick={(e) => {
                  e.stopPropagation()
                  // Desabilitado - apenas visualização
                }}
                disabled
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                title="Mais opções"
                onClick={(e) => {
                  e.stopPropagation()
                  // Desabilitado - apenas visualização
                }}
                disabled
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Corpo da Coluna com scroll vertical */}
      <div 
        className="flex-1 overflow-y-auto overflow-x-hidden px-2" 
        style={{ 
          scrollbarWidth: 'thin', 
          scrollBehavior: 'smooth',
          minHeight: 0,
          // Garantir que o scroll funcione corretamente
          WebkitOverflowScrolling: 'touch',
          // Padding superior e inferior para garantir que todos os cards sejam visíveis
          paddingTop: '8px',
          paddingBottom: '48px' // Padding maior no final para garantir que o último card seja totalmente visível
        }}
      >
        <div className="space-y-2" style={{ paddingBottom: '48px' }}>
          {projetos.length > 0 ? (
            <>
              {projetos.map(projeto => (
                <KanbanCard
                  key={projeto.id}
                  projeto={projeto}
                  onClick={onCardClick}
                  isReadOnly={isReadOnly}
                />
              ))}
              {/* Espaço extra no final para garantir que o último card seja totalmente visível */}
              <div style={{ height: '48px', flexShrink: 0, minHeight: '48px', width: '100%' }} />
            </>
          ) : (
            <div className="text-center text-gray-400 text-xs py-8 px-4">
              <p>Nenhum card</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
